import React, { useEffect, useState } from "react";
import {
  Box,
  ColumnLayout,
  Container,
  Flashbar,
  StatusIndicator,
} from "@cloudscape-design/components";
import { useParams } from "react-router-dom";
import DetailOverview from "./DetailOverview";
import { generateClient } from "aws-amplify/api";
import { ListVehiclesQueryVariables } from "../../../../API";
import { listVehicleAlarms, listVehicles } from "../../../../graphql/queries";
import { VehicleAlarms } from "../../w_vehicle-alarms/data/data";

interface FlashbarItem {
  type: "info" | "warning" | "error" | "success"; // Adjust according to your requirements
  dismissible: boolean;
  dismissLabel: string;
  onDismiss: () => void;
  content: React.ReactNode;
  id: string;
}

interface CellProps {
  Alarm: string | number;
}

const AlarmCell: React.FC<CellProps> = ({ Alarm }) => {
  let statusType: "success" | "error" | "warning" | "info" = "info"; // status types (status indicator cloudscape)

  // Conditional rendering logic
  if (Alarm === 28) {
    statusType = "success";
  } else if (Alarm === 27) {
    statusType = "error";
  } else if (Alarm === 23) {
    statusType = "warning";
  } else {
    statusType = "info";
  }

  return (
    <StatusIndicator type={statusType}>
      {statusType === "success"
        ? "Operational Normal"
        : statusType === "error"
        ? "Battery Low"
        : statusType === "warning"
        ? "No GPS"
        : statusType === "info"
        ? "Data Error"
        : "Unknown Status"}
    </StatusIndicator>
  );
};

const SummaryDetail: React.FC = () => {
  const { VehicleCode } = useParams<{ VehicleCode: string }>();
  const [vehicleList, setVehicleList] = useState<VehicleAlarms[]>([]);
  const [items, setItems] = useState<FlashbarItem[]>([]);

  useEffect(() => {
    fetchVehicle();
  }, []);

  const fetchVehicle = async (limit = 100) => {
    try {
      const client = generateClient();
      let nextToken: string | null = null;
      let allVehicles: VehicleAlarms[] = [];

      do {
        const variables: ListVehiclesQueryVariables = { limit, nextToken };
        let response = await client.graphql({
          query: listVehicleAlarms,
          variables,
        });

        const vehicles = response.data.listVehicleAlarms.items;
        nextToken = response.data.listVehicleAlarms.nextToken ?? null; // Ensure nextToken is either string or null

        const newVehicles: VehicleAlarms[] = vehicles.map((vehicle: any) => ({
          id: vehicle.id,
          VehicleCode: vehicle.vehiclecode ?? "",
          TypeVehicle: vehicle.typevehicle ?? "",
          Location: vehicle.location ?? "",
          Status: Boolean(vehicle.status),
          Timestamps: vehicle.timestamps ?? "",
          Alarm: vehicle.alarm ?? 0, // Add Alarm field
        }));

        allVehicles = [...allVehicles, ...newVehicles];
      } while (nextToken);

      // Filter vehicles by VehicleCode
      const filteredVehicles = allVehicles.filter(
        (vehicle) => vehicle.VehicleCode === VehicleCode
      );
      setVehicleList(filteredVehicles);

      // Update Flashbar item with the latest timestamp
      if (filteredVehicles.length > 0) {
        const latestTimestamp = new Date(
          Math.max(...filteredVehicles.map((v) => new Date(v.Timestamps).getTime()))
        ).toLocaleString();

        setItems([
          {
            type: "info",
            dismissible: true,
            dismissLabel: "Dismiss message",
            onDismiss: () => setItems([]),
            content: <>Last Updated data received at {latestTimestamp}</>,
            id: "message_1",
          },
        ]);
      }
    } catch (err) {
      console.log("error fetching vehicles: ", err);
    }
  };

  return (
    <Container>
      <ColumnLayout columns={2} minColumnWidth={500}>
        <div>
          <div>
            <h1>Detail Unit</h1>
            <h3>{VehicleCode}</h3>
          </div>
          <div style={{ marginBottom: 20 }}>
            <Flashbar items={items} />
          </div>
          {vehicleList.length > 0 ? (
            vehicleList.map((vehicle) => (
              <ColumnLayout key={vehicle.id} borders="horizontal" columns={2}>
                <div>Vehicle ID</div>
                <div style={{ fontWeight: "bold" }}>{vehicle.id}</div>
                <div>Vehicle Code</div>
                <div style={{ fontWeight: "bold" }}>{vehicle.VehicleCode}</div>
                <div>Current Alarm</div>
                <div>
                  <AlarmCell Alarm={vehicle.Alarm} />
                </div>
              </ColumnLayout>
            ))
          ) : (
            <div>No vehicle data found.</div>
          )}
        </div>

        <DetailOverview />
      </ColumnLayout>
    </Container>
  );
};

export default SummaryDetail;
